import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2, Droplets, Paintbrush, PaintRoller, Grid3x3, Hammer, HelpCircle,
  ArrowRight, ArrowLeft, CheckCircle2, Loader2,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
  { icon: Building2, label: "Ravalement", desc: "Façade complète remise à neuf" },
  { icon: Droplets, label: "Nettoyage hydrofuge", desc: "Haute pression + protection" },
  { icon: Paintbrush, label: "Peinture intérieure", desc: "Murs, plafonds, boiseries" },
  { icon: PaintRoller, label: "Peinture extérieure", desc: "Façades, volets, portails" },
  { icon: Grid3x3, label: "Carrelage", desc: "Sols, murs, salles de bain" },
  { icon: Hammer, label: "Petite maçonnerie", desc: "Fissures, reprises, seuils" },
  { icon: HelpCircle, label: "Autre projet", desc: "Décrivez-nous votre idée" },
];

const CONDITIONS = ["Bon état", "À rénover", "Très dégradé", "Je ne sais pas"];
const TIMELINES = ["Dès que possible", "Dans 1 mois", "Dans 3 mois", "Dans 6 mois", "Pas pressé"];

const PHONE_RE = /^(?:\+33|0)[1-9](?:[\s.-]?\d{2}){4}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inputCls =
  "w-full rounded-[10px] border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-tertiary focus:outline-none focus:border-brand-royal focus:ring-2 focus:ring-brand-royal/20 transition-colors";
const labelCls = "block text-sm font-semibold text-brand-deep mb-2";

export default function DevisPage() {
  const [step, setStep] = useState(1);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    projectType: "", city: "", postalCode: "", surface: "", condition: "", timeline: "", details: "",
    lastName: "", firstName: "", phone: "", email: "", rgpd: false,
  });

  const set = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const validateStep2 = () => {
    const e = {};
    if (!data.city.trim()) e.city = "Indiquez votre ville";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep3 = () => {
    const e = {};
    if (!data.lastName.trim()) e.lastName = "Votre nom est requis";
    if (!data.firstName.trim()) e.firstName = "Votre prénom est requis";
    if (!PHONE_RE.test(data.phone.trim())) e.phone = "Numéro de téléphone français invalide (ex : 06 12 34 56 78)";
    if (!EMAIL_RE.test(data.email.trim())) e.email = "Adresse email invalide";
    if (!data.rgpd) e.rgpd = "Vous devez accepter pour être recontacté";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validateStep3()) return;
    setSending(true);
    try {
      await axios.post(`${API}/devis`, {
        project_type: data.projectType,
        city: data.city.trim(),
        postal_code: data.postalCode.trim(),
        surface: data.surface ? Number(data.surface) : null,
        condition: data.condition,
        timeline: data.timeline,
        details: data.details.trim(),
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        phone: data.phone.trim(),
        email: data.email.trim(),
        rgpd: data.rgpd,
      });
      setSent(true);
      window.scrollTo({ top: 0 });
    } catch {
      setErrors({ submit: "Une erreur est survenue. Réessayez ou appelez-nous au 06 41 96 83 46." });
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <>
        <Seo title="Demande envoyée | HRC Rénovation" description="Votre demande de devis a bien été reçue. Brandon vous rappelle sous 24h ouvrées." />
        <section className="pt-[140px] pb-24 min-h-[70vh] flex items-center bg-surface-canvas">
          <div className="max-w-[560px] mx-auto px-6 text-center" data-testid="devis-confirmation">
            <CheckCircle2 size={72} className="text-success mx-auto animate-in fade-in zoom-in duration-500" strokeWidth={1.5} />
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-brand-deep mt-8">
              Merci {data.firstName}, votre demande est bien reçue.
            </h1>
            <p className="mt-4 text-lg text-ink-secondary">Brandon vous rappelle sous 24h ouvrées.</p>
            <Link to="/" className="btn-base btn-metal mt-10" data-testid="devis-back-home-btn">Retour à l'accueil</Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Seo
        title="Devis gratuit sous 48h | HRC Rénovation Dordogne"
        description="Demandez votre devis ravalement, hydrofuge, peinture ou carrelage en Dordogne. Réponse sous 48h. ☎ 06 41 96 83 46."
      />
      <section className="pt-[120px] pb-24 bg-surface-canvas min-h-screen">
        <div className="max-w-[720px] mx-auto px-5 lg:px-6">
          <Reveal>
            <p className="micro-label mb-4 text-center">Devis gratuit</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-brand-deep text-center" style={{ letterSpacing: "-0.03em" }} data-testid="devis-h1">
              Votre devis en 2 minutes.
            </h1>
            <p className="mt-4 text-ink-secondary text-center">Gratuit, sans engagement. Réponse sous 48h.</p>

            {/* Barre de progression */}
            <div className="mt-10 mb-10">
              <div className="flex justify-between text-[13px] font-semibold text-ink-tertiary mb-2">
                <span className={step >= 1 ? "text-brand-royal" : ""}>1. Projet</span>
                <span className={step >= 2 ? "text-brand-royal" : ""}>2. Chantier</span>
                <span className={step >= 3 ? "text-brand-royal" : ""}>3. Coordonnées</span>
              </div>
              <div className="h-2 bg-line rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${(step / 3) * 100}%`, background: "var(--gradient-metal)" }} data-testid="devis-progress-bar" />
              </div>
            </div>

            {/* Étape 1 */}
            {step === 1 && (
              <div data-testid="devis-step-1">
                <p className={labelCls}>Quel est votre projet ?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {PROJECT_TYPES.map(({ icon: Icon, label, desc }) => {
                    const selected = data.projectType === label;
                    return (
                      <button
                        key={label}
                        type="button"
                        onClick={() => set("projectType", label)}
                        data-testid={`devis-type-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                        className={`text-left rounded-xl border-2 p-5 transition-all duration-200 ${selected ? "border-brand-royal bg-brand-highlight" : "border-line bg-white hover:border-brand-metallic"}`}
                      >
                        <Icon size={24} className={selected ? "text-brand-royal" : "text-ink-secondary"} aria-hidden="true" />
                        <p className="font-heading font-semibold text-brand-deep mt-3">{label}</p>
                        <p className="text-[13px] text-ink-secondary mt-1">{desc}</p>
                      </button>
                    );
                  })}
                </div>
                <button
                  type="button"
                  disabled={!data.projectType}
                  onClick={() => setStep(2)}
                  className="btn-base btn-metal w-full mt-8 disabled:opacity-40 disabled:pointer-events-none"
                  data-testid="devis-step1-next-btn"
                >
                  Continuer
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              </div>
            )}

            {/* Étape 2 */}
            {step === 2 && (
              <div className="space-y-6" data-testid="devis-step-2">
                <div className="grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4">
                  <div>
                    <label className={labelCls} htmlFor="devis-city">Ville *</label>
                    <input id="devis-city" type="text" className={inputCls} placeholder="Ex : Périgueux" value={data.city} onChange={(e) => set("city", e.target.value)} data-testid="devis-city-input" />
                    {errors.city && <p className="text-red-600 text-[13px] mt-1.5">{errors.city}</p>}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="devis-cp">Code postal</label>
                    <input id="devis-cp" type="text" inputMode="numeric" className={inputCls} placeholder="24000" value={data.postalCode} onChange={(e) => set("postalCode", e.target.value)} data-testid="devis-postal-input" />
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="devis-surface">Surface approximative : <span className="text-brand-royal tnum">{data.surface || 0} m²</span></label>
                  <input id="devis-surface" type="number" min="0" max="500" className={inputCls} placeholder="Ex : 120" value={data.surface} onChange={(e) => set("surface", e.target.value)} data-testid="devis-surface-input" />
                  <input type="range" min="0" max="500" step="10" value={data.surface || 0} onChange={(e) => set("surface", e.target.value)} className="w-full mt-3 accent-[#1E4FA8]" aria-label="Surface en m²" />
                </div>

                <div>
                  <p className={labelCls}>État du support</p>
                  <div className="flex flex-wrap gap-2.5">
                    {CONDITIONS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => set("condition", c)}
                        className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${data.condition === c ? "border-brand-royal bg-brand-highlight text-brand-royal" : "border-line bg-white text-ink-secondary hover:border-brand-metallic"}`}
                        data-testid={`devis-condition-${c.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls} htmlFor="devis-timeline">Délai souhaité</label>
                  <select id="devis-timeline" className={inputCls} value={data.timeline} onChange={(e) => set("timeline", e.target.value)} data-testid="devis-timeline-select">
                    <option value="">Choisissez…</option>
                    {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                <div>
                  <label className={labelCls} htmlFor="devis-details">Précisions (optionnel)</label>
                  <textarea id="devis-details" rows={4} maxLength={400} className={inputCls} placeholder="Décrivez votre projet en quelques mots…" value={data.details} onChange={(e) => set("details", e.target.value)} data-testid="devis-details-textarea" />
                  <p className="text-[12px] text-ink-tertiary mt-1 text-right tnum">{data.details.length}/400</p>
                </div>

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(1)} className="btn-base btn-outline-deep flex-none" data-testid="devis-step2-back-btn">
                    <ArrowLeft size={18} aria-hidden="true" />
                    Retour
                  </button>
                  <button type="button" onClick={() => { if (validateStep2()) setStep(3); }} className="btn-base btn-metal flex-1" data-testid="devis-step2-next-btn">
                    Continuer
                    <ArrowRight size={18} aria-hidden="true" />
                  </button>
                </div>
              </div>
            )}

            {/* Étape 3 */}
            {step === 3 && (
              <div className="space-y-5" data-testid="devis-step-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls} htmlFor="devis-lastname">Nom *</label>
                    <input id="devis-lastname" type="text" className={inputCls} placeholder="Dupont" value={data.lastName} onChange={(e) => set("lastName", e.target.value)} data-testid="devis-lastname-input" />
                    {errors.lastName && <p className="text-red-600 text-[13px] mt-1.5">{errors.lastName}</p>}
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="devis-firstname">Prénom *</label>
                    <input id="devis-firstname" type="text" className={inputCls} placeholder="Marie" value={data.firstName} onChange={(e) => set("firstName", e.target.value)} data-testid="devis-firstname-input" />
                    {errors.firstName && <p className="text-red-600 text-[13px] mt-1.5">{errors.firstName}</p>}
                  </div>
                </div>
                <div>
                  <label className={labelCls} htmlFor="devis-phone">Téléphone *</label>
                  <input id="devis-phone" type="tel" className={inputCls} placeholder="06 12 34 56 78" value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid="devis-phone-input" />
                  {errors.phone && <p className="text-red-600 text-[13px] mt-1.5">{errors.phone}</p>}
                </div>
                <div>
                  <label className={labelCls} htmlFor="devis-email">Email *</label>
                  <input id="devis-email" type="email" className={inputCls} placeholder="marie.dupont@email.fr" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid="devis-email-input" />
                  {errors.email && <p className="text-red-600 text-[13px] mt-1.5">{errors.email}</p>}
                </div>

                <label className="flex items-start gap-3 cursor-pointer bg-white border border-line rounded-xl p-4">
                  <input type="checkbox" checked={data.rgpd} onChange={(e) => set("rgpd", e.target.checked)} className="mt-1 w-4 h-4 accent-[#1E4FA8]" data-testid="devis-rgpd-checkbox" />
                  <span className="text-[13.5px] text-ink-secondary">
                    J'accepte que mes données soient utilisées pour être recontacté par HRC Rénovation.{" "}
                    <Link to="/politique-confidentialite" className="text-brand-royal underline underline-offset-2">Voir la politique de confidentialité</Link>.
                  </span>
                </label>
                {errors.rgpd && <p className="text-red-600 text-[13px]">{errors.rgpd}</p>}
                {errors.submit && <p className="text-red-600 text-sm font-medium" data-testid="devis-submit-error">{errors.submit}</p>}

                <div className="flex gap-3">
                  <button type="button" onClick={() => setStep(2)} className="btn-base btn-outline-deep flex-none" data-testid="devis-step3-back-btn">
                    <ArrowLeft size={18} aria-hidden="true" />
                    Retour
                  </button>
                  <button type="button" onClick={submit} disabled={sending} className="btn-base btn-metal flex-1 disabled:opacity-60" data-testid="devis-submit-btn">
                    {sending ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : null}
                    {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                  </button>
                </div>
              </div>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
